<template>
  <module-template id="scrolltainer" :title="$t('global.run.compare-runs')">
    <v-card class="basic-card-horizontal mb-3" rounded shaped max-width="1000">
      <v-row dense>
        <v-col class="basic-card-horizontal-left py-0 pl-2">
          <span> {{ $t("global.run.run") }} </span>
        </v-col>

        <v-col class="py-0 px-1" v-for="id in runIdsArray" :key="id">
          <div class="pr-2">
            <strong> {{ "#" + id }} </strong>
          </div>
        </v-col>
      </v-row>

      <v-row dense>
        <v-col class="basic-card-horizontal-left py-0 pl-2">
          <span>Module</span>
        </v-col>

        <v-col
          class="py-0 px-1"
          v-for="(module, i) in comparisonOverview?.modules"
          :key="i"
        >
          <div class="pr-2">
            <span
              :style="comparisonOverview?.modulesDifferent ? 'color: red' : ''"
            >
              {{ module }}
            </span>

            <span
              :style="
                comparisonOverview?.moduleVersionsDifferent ? 'color: red' : ''
              "
            >
              {{ " (v." + comparisonOverview?.moduleVersions[i] + ")" }}
            </span>
          </div>
        </v-col>
      </v-row>

      <v-row dense>
        <v-col class="basic-card-horizontal-left py-0 pl-2">
          <span>{{ $t("global.concepts.status") }}</span>
        </v-col>

        <v-col
          class="py-0 px-1"
          v-for="(status, i) in comparisonOverview?.statuses"
          :key="i"
        >
          <div class="pr-2">
            <span
              :style="comparisonOverview?.statusesDifferent ? 'color: red' : ''"
            >
              {{ status }}
            </span>
          </div>
        </v-col>
      </v-row>

      <v-row dense>
        <v-col class="basic-card-horizontal-left py-0 pl-2">
          <span> Simulation </span>
        </v-col>

        <v-col
          class="py-0 px-1"
          v-for="(sim, i) in comparisonOverview?.simulations"
          :key="i"
        >
          <div class="pr-2">
            <span
              :style="
                comparisonOverview?.simulationsDifferent ? 'color: red' : ''
              "
            >
              {{ sim }}
            </span>

            <span
              :style="
                comparisonOverview?.simulationVersionsDifferent
                  ? 'color: red'
                  : ''
              "
            >
              {{ " (v." + comparisonOverview?.simulationVersions[i] + ")" }}
            </span>
          </div>
        </v-col>
      </v-row>
    </v-card>

    <v-card>
      <v-card-text>
        <v-row>
          <v-col>
            <h6 class="py-4">
              {{ $t("global.run.input-files") }}
            </h6>
          </v-col>

          <v-col>
            <v-switch
              v-model="showAll"
              class="v-input--reverse mt-4"
              dense
              hide-details
              style="float: right"
              @change="onShowAll"
            >
              <template #label>
                <span style="color: black">{{
                  $t("global.run.show-all")
                }}</span>
              </template>
            </v-switch>
          </v-col>
        </v-row>

        <span v-if="fileItemsFiltered?.length === 0 && !fileBusy">
          {{ $t("global.msg.no-diffs") }}</span
        >

        <ooliba-basic-table
          v-else
          :headers="fileHeaders"
          :items="fileItemsFiltered"
          :loading="fileBusy"
          no-pagination
          :height="tableHeight(63, fileItemsFiltered?.length)"
        >
          <template v-for="id in runIdsArray" #[`item.${id}`]="{ item }">
            <span :key="id" v-if="!item[id]"> - </span>

            <router-link
              v-else
              :key="id"
              :to="`/file/view/${item[id + 'id']}/${item[id + 'version']}`"
              :style="fileVersionLinkStyle(item)"
              >{{ item[id] }}</router-link
            >

            <v-chip
              :key="id + 'env'"
              small
              outlined
              v-if="item[id + 'env']?.localBp"
              class="ml-2"
              >{{ item[id + "env"]?.name }}</v-chip
            >
            <file-env-chip
              :key="id + 'env'"
              v-if="item[id + 'env'] && !item[id + 'env']?.localBp"
              :color="item[id + 'env'].color"
              :label="item[id + 'env'].label"
              class="ml-2"
            >
            </file-env-chip>
          </template>

          <template #[`item.compare`]="{ item }">
            <v-icon
              v-if="
                isExcelFile(item.name) &&
                item.isDifferent &&
                item.allFilesExist &&
                !item.isComparing &&
                !item.isCompared
              "
              @click="onCompare(item)"
              >mdi-compare-horizontal</v-icon
            >
            <v-progress-circular
              :size="23"
              v-if="item.isComparing"
              indeterminate
            />
            <v-icon
              v-if="item.isCompared && !item.maxSizeExceeded"
              @click="onOpenHtml(item)"
              class="mr-1"
              >mdi-eye-outline</v-icon
            >
            <v-icon v-if="item.isCompared" @click="onDownloadComparison(item)"
              >pic-import</v-icon
            >
          </template>
        </ooliba-basic-table>
      </v-card-text>
    </v-card>

    <v-dialog v-model="showComparison" persistent>
      <v-overlay absolute :value="isLoadingComparison">
        <v-chip color="blue" dark>{{ $t("global.msg.loading") }}</v-chip>
      </v-overlay>

      <v-card>
        <v-icon
          large
          @click="onCloseComparison"
          color="error"
          style="float: right"
        >
          mdi-alpha-x-box-outline</v-icon
        >

        <v-card-text>
          <iframe
            :src="comparisonUrl"
            width="100%"
            title="Comparison"
            class="mt-1"
            ref="comparisonHtml"
            style="height: 74vh"
            v-if="showComparison"
            @load="handleComparisonLoaded"
          ></iframe>
        </v-card-text>
      </v-card>
    </v-dialog>
  </module-template>
</template>

<script>
import { get, downloadFile } from "@/model/api";
import ModuleTemplate from "@/components/layout/ModuleTemplate";
import OolibaBasicTable from "@/components/OolibaBasicTable";
import FileEnvChip from "@/components/FileEnvChip";
import { tableHeight, isExcelFile } from "@/model/util";

export default {
  name: "RunComparison",

  components: {
    ModuleTemplate,
    OolibaBasicTable,
    FileEnvChip,
  },

  data() {
    return {
      fileHeaders: [{ text: this.$t("global.concepts.name"), value: "name" }],
      fileItems: [],
      fileItemsFiltered: [],
      fileBusy: false,

      comparisonOverview: {},

      showComparison: false,
      isLoadingComparison: false,
      comparisonUrl: "",
      showAll: false,
      excelFileId1: undefined,
      excelVersionId1: undefined,
      excelFileId2: undefined,
      excelVersion2: undefined,

      runIdsArray: [],
    };
  },

  methods: {
    tableHeight,

    isExcelFile,

    fileVersionLinkStyle(item) {
      return item.isDifferent && item.allFilesExist
        ? "color: red"
        : "color: #4472c4";
    },

    onShowAll() {
      if (this.showAll) {
        this.fileItemsFiltered = this.fileItems;
      } else {
        this.fileItemsFiltered = this.fileItems.filter((c) => c.isDifferent);
      }
    },

    handleComparisonLoaded() {
      const iframe = this.$refs.comparisonHtml;
      const pre = iframe.contentDocument.querySelector("body > pre");
      if (pre) {
        const serverError = pre.textContent;
        if (serverError.includes("Max size exceeded")) {
          this.comparisonUrl = "";
          this.$store.commit("showWarning", this.$t("global.msg.fichierGrand"));
        } else if (serverError.includes("File not found or empty")) {
          this.comparisonUrl = "";
          this.$store.commit(
            "showWarning",
            this.$t("global.msg.missingEmptyTemplateFile")
          );
        }
      }
      this.isLoadingComparison = false;
    },

    onCloseComparison() {
      this.showComparison = false;

      // Switching tabs in the Excel preview inserts elements into the history stack, so we need to remove these when the preview is closed
      // OOL-2629
      get(
        `/file/comparison-html-sheet-requests/${this.excelFileId1}/${this.excelVersion1}/${this.excelFileId2}/${this.excelVersion2}`
      )
        .then((r) => {
          if (r > 1) {
            window.history.go(-r + 1);
          }
        })
        .catch((err) => this.$store.commit("showError", err));
    },

    onCompare(item) {
      item.isComparing = true;
      const runId1 = this.runIdsArray[0];
      const fileId1 = item[runId1 + "id"];
      const version1 = item[runId1 + "version"] - 1;

      const runId2 = this.runIdsArray[1];
      const fileId2 = item[runId2 + "id"];
      const version2 = item[runId2 + "version"] - 1;

      get(
        `/file/generate-comparison/${fileId1}/${version1}/${fileId2}/${version2}`
      )
        .then((r) => {
          item.maxSizeExceeded = r;
          item.isComparing = false;
          item.isCompared = true;
        })
        .catch((e) => {
          this.$store.commit("showError", e);
          item.isComparing = false;
        });
    },

    onOpenHtml(item) {
      this.isLoadingComparison = true;
      const runId1 = this.runIdsArray[0];
      this.excelFileId1 = item[runId1 + "id"];
      this.excelVersion1 = item[runId1 + "version"] - 1;

      const runId2 = this.runIdsArray[1];
      this.excelFileId2 = item[runId2 + "id"];
      this.excelVersion2 = item[runId2 + "version"] - 1;

      this.comparisonUrl = `/compliance-server/api/file/comparison-html/${this.excelFileId1}/${this.excelVersion1}/${this.excelFileId2}/${this.excelVersion2}/index.html`;
      this.showComparison = true;
    },

    onDownloadComparison(item) {
      const runId1 = this.runIdsArray[0];
      const fileId1 = item[runId1 + "id"];
      const version1 = item[runId1 + "version"] - 1;

      const runId2 = this.runIdsArray[1];
      const fileId2 = item[runId2 + "id"];
      const version2 = item[runId2 + "version"] - 1;

      downloadFile(
        `/file/download-comparison/${fileId1}/${version1}/${fileId2}/${version2}`
      ).catch((err) => {
        this.$store.commit("showError", err);
      });
    },
  },

  async created() {
    const runIds = this.$route.query.runIds;
    this.runIdsArray = runIds?.split(",");
    if (this.runIdsArray?.length > 1) {
      this.runIdsArray.forEach((id) => {
        const headerText = this.$t("global.run.run") + " #" + id;
        this.fileHeaders.push({
          text: headerText,
          value: id,
        });
      });

      this.comparisonOverview = await get(
        "/simulationRun/comparison-overview?runIds=" + runIds
      ).catch((e) => this.$store.commit("showError", e));

      this.fileBusy = true;
      if (this.runIdsArray.length === 2) {
        this.fileHeaders.push({
          text: this.$t("global.run.compare"),
          value: "compare",
          align: "center",
        });
      }
      const comparisons = await get(
        "/simulationRun/run-comparison?runIds=" + runIds
      ).catch((e) => this.$store.commit("showError", e));

      if (comparisons) {
        this.fileItems = comparisons.map((c) => {
          const item = {};
          item.name = c.name;
          item.isDifferent = c.isDifferent;
          item.allFilesExist = c.allFilesExist;
          item.isComparing = false;
          item.isCompared = false;

          c.versions.forEach((v, i) => {
            if (v === -1) return;
            const runId = this.runIdsArray[i];
            item[runId] = "Version " + (v + 1);
            item[runId + "version"] = v + 1;
            item[runId + "id"] = c.ids[i];
          });

          if (c.envsDifferent) {
            c.environments.forEach((e, i) => {
              const runId = this.runIdsArray[i];
              item[runId + "env"] = e;
            });
          }
          return item;
        });
      }
      if (this.showAll) {
        this.fileItemsFiltered = this.fileItems;
      } else {
        this.fileItemsFiltered = this.fileItems.filter((c) => c.isDifferent);
      }
      this.fileBusy = false;
    }
  },
};
</script>
